import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Alert } from 'us-react-components/dist/Alert/Alert';
import { Carousel } from 'us-react-components/dist/Carousel/Carousel';
import { LogoSpinner } from 'us-react-components/dist/LogoSpinner/LogoSpinner';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { ReviewCard } from 'us-react-components/dist/Carousel/components/ReviewCard';
import { TextArea } from 'us-react-components/dist/TextArea/TextArea';
import { axios, JobAvailabilityType, JobExample, JobExamplesService, JobService, SignupReasonId } from 'us-web-services';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';
import { truncateTextToSentence } from '../../../utils/StringUtil';

const imageUrls = [
  'https://assets.urbansitter.com/static/img/flower/family-default.webp',
  'https://assets.urbansitter.com/static/img/flower/caregiver-ziwase.webp',
  'https://assets.urbansitter.com/static/img/flower/caregiver-poppy.webp',
  'https://assets.urbansitter.com/static/img/flower/family-testimonal.webp',
];

const DESCRIPTION_MAX_LENGTH = 2500;
const EXAMPLE_MAX_LENGTH = 320;

export const Details = observer((): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [examples, setExamples] = useState<JobExample[]>([]);
  const [inputFocused, setInputFocused] = useState(false);
  const [remainingCharacters, setRemainingCharacters] = useState(DESCRIPTION_MAX_LENGTH);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores: { familyRegistration, parent }, services: { tracker } } = useDependencies();

  const getSignupReasonId = (availabilityTypeCode?: string): number => {
    switch (availabilityTypeCode) {
      case JobAvailabilityType.occasional:
        return SignupReasonId.ONE_TIME;
      case JobAvailabilityType.partTime:
        return SignupReasonId.PART_TIME;
      case JobAvailabilityType.fullTime:
        return SignupReasonId.FULL_TIME;
      default:
        return SignupReasonId.ONE_TIME;
    }
  };

  const hasJobData = (): boolean => !!familyRegistration.jobType && !!familyRegistration.startDate && !!familyRegistration.lookingForId;

  const hasLocationData = (): boolean => {
    const { address } = parent.parent;
    return !!address?.zip && !!address?.state && !!address?.city;
  };

  familyRegistration.setCurrentRoute(Routes.parent.registration.details);

  useEffect(() => {
    if (!hasJobData()) {
      if (hasLocationData()) {
        navigate(Routes.parent.dashboard);
      } else {
        navigate(Routes.parent.registration.gettingStarted);
      }
    } else {
      const fetchExamples = async (): Promise<void> => {
        try {
          const config = {
            params: {
              verticalTypeId: familyRegistration.lookingForId,
              availabilityTypeId: getSignupReasonId(familyRegistration.availabilityType),
            },
          };

          const exampleResponse = (await JobExamplesService.getJobExamples(config)).data;
          setExamples(exampleResponse.data);
        } catch (error) {
          console.warn('Error fetching examples', error);
        } finally {
          setLoading(false);
        }
      };

      fetchExamples();
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value } = e.target;

    if (value.length <= DESCRIPTION_MAX_LENGTH) {
      setRemainingCharacters(DESCRIPTION_MAX_LENGTH - value.length);
      familyRegistration.setDetails(value);
    }
  };

  const handleContinue = async (): Promise<void> => {
    setSubmitting(true);

    try {
      const response = (await JobService.create(familyRegistration.job)).data;
      familyRegistration.setJobId(response.data.id);
      navigate(Routes.parent.registration.success);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.errors) {
        toast.error(e.response.data.errors[0].message);
      } else {
        console.warn(e);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isContinueDisabled = (): boolean => loading || submitting || familyRegistration.details.length < 10;

  const textDirtyAndNotLongEnough = (familyRegistration.details.length >= 1 && familyRegistration.details.length < 10) && !inputFocused;

  const onBlur = (): void => {
    setInputFocused(false);
  };

  const onFocus = (): void => {
    setInputFocused(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      e.stopPropagation(); // don't submit the form; allow line breaks in the textarea
    }
  };

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={t('pages.nux.family.details.title')}
      onContinue={handleContinue}
      isContinueDisabled={isContinueDisabled()}>
      {loading ? <LogoSpinner /> :
        (
          <div className='flex flex-col px-4 md:px-12 w-full'>
            <div className='flex flex-col px-8'>
              <TextArea
                id='job-details-text'
                label={t('pages.nux.family.details.label')}
                className='sm:min-h-[80px]'
                maxLength={DESCRIPTION_MAX_LENGTH}
                onChange={handleChange}
                onFocus={onFocus} onBlur={onBlur}
                onKeyDown={onKeyDown}
                value={familyRegistration.details}
              />
              <Alert className='w-full' color='error' isOpen={textDirtyAndNotLongEnough}>
                {t('pages.nux.family.details.tooShort')}
              </Alert>
              <div className='flex inline-flex justify-between text-xs pb-5 pt-2'>
                <div className='pr-7'>
                  {t('pages.nux.family.details.privacy')}
                </div>
                <div className='text-right'>
                  {remainingCharacters > 0 && (<div className='text-gray-500'>{remainingCharacters} {t('pages.nux.family.details.charactersRemaining')}</div>)}
                  {remainingCharacters === 0 && (<div className='text-yellow-500'>{t('pages.nux.family.details.charLimitReached')}</div>)}
                  {remainingCharacters < 0 && (<div className='text-red-500'>{t('pages.nux.family.details.charLimitExceeded')}</div>)}
                </div>
              </div>
              <p className='text-center pb-2 md:pt-4 font-semibold text-sm'>{t('pages.nux.family.details.examples')}</p>
            </div>
            {examples && examples.length > 0 &&
              (
                <div className='max-w-full h-[180px] md:w-[395px] md:border-[1px] md:border-neutral-200 self-center mb-6'>
                  <Carousel>
                    {/* note, to show more than four examples we'll need to unbound it here and add extra imageUrls */}
                    {examples.slice(0, 4).map((example, index) => (
                      <ReviewCard key={example.id} imageUrl={imageUrls[index]} content={truncateTextToSentence(example.description, EXAMPLE_MAX_LENGTH)} />
                    ))}
                  </Carousel>
                </div>
              )
            }
          </div>
        )}
    </NuxContainer>
  )
});
